import type { Timestamp } from '@anytracker/core'
import { useState } from 'react'

import { Sheet, SheetOptionGroup, type SheetOption } from '../../components/Sheet.js'
import { formatDate, formatTime } from '../../format/dates.js'
import styles from './LogFlow.module.css'

interface Props {
  readonly open: boolean
  readonly at: Timestamp
  readonly now: Timestamp
  readonly onChange: (at: Timestamp) => void
  readonly onClose: () => void
}

/** The offsets the redesign draws. They cover almost every real backdating. */
const QUICK = [0, 15, 30, 60, 180] as const

/** Anything more than a minute out is a deliberate backdate, not clock drift. */
const BACKDATED_MS = 60_000

function label(minutes: number): string {
  if (minutes === 0) {
    return 'Now'
  }
  if (minutes < 60) {
    return `${String(minutes)} min ago`
  }
  const hours = minutes / 60
  return hours === 1 ? '1 hour ago' : `${String(hours)} hours ago`
}

/**
 * When it happened, in a sheet (T-803, T-805).
 *
 * The quick offsets are the drawn control and the one that matters: most
 * backdating is "I forgot for twenty minutes", and reaching for a date picker
 * to record something from half an hour ago is the friction that stops logging
 * happening at all.
 *
 * **A "Custom" option opens the exact picker.** The redesign draws only the
 * offsets, and the furthest is three hours — so as drawn there is no way to
 * record last night at all. Rather than sit a date picker under the offsets and
 * make the common case look like the hard one, it is one more pill in the same
 * row, and choosing it reveals the picker. `Custom` stays lit whenever the time
 * is not one of the offsets, so reopening a backdated entry says where its time
 * came from.
 *
 * The **zone is preserved** through every adjustment. Backdating changes when,
 * not where — an entry corrected an hour later was still recorded in the zone
 * the user was standing in (ADR-0006).
 */
export function TimeSheet({ open, at, now, onChange, onClose }: Props) {
  /* Whether the exact picker is showing. Closing the sheet forgets it. */
  const [custom, setCustom] = useState(false)

  /** True when the current time is not one of the offsets above. */
  const matchesAnOffset = QUICK.some(
    (minutes) => Math.abs(at.epochMs - (now.epochMs - minutes * 60_000)) < 30_000,
  )

  const offsets: readonly SheetOption[] = QUICK.map((minutes) => {
    const epochMs = now.epochMs - minutes * 60_000
    return {
      id: String(minutes),
      label: label(minutes),
      // Within half a minute counts as "this one", so the chosen offset stays
      // lit while the clock moves on underneath it.
      selected: !custom && Math.abs(at.epochMs - epochMs) < 30_000,
      onChoose: () => {
        setCustom(false)
        onChange({ epochMs, timeZone: at.timeZone })
        onClose()
      },
    }
  })

  const options: readonly SheetOption[] = [
    ...offsets,
    {
      id: 'custom',
      label: 'Custom',
      // Lit whenever the time is not one of the offsets, so a sheet reopened on
      // a backdated entry shows where that time came from rather than nothing.
      selected: custom || !matchesAnOffset,
      onChoose: () => {
        setCustom(true)
      },
    },
  ]

  return (
    // "Change the time" rather than repeating the group's own question below
    // it, which is what the prototype does and what reads without an echo.
    <Sheet
      open={open}
      title="Change the time"
      onClose={() => {
        setCustom(false)
        onClose()
      }}
    >
      <SheetOptionGroup label="When did it happen?" options={options} />

      {/*
        Behind "Custom" rather than always on show.

        The offsets are the answer nineteen times in twenty, and a date picker
        sitting under them makes the common case look like the hard one. But it
        has to exist: the furthest offset is three hours, so without this there
        is no way to record last night at all.
      */}
      {(custom || !matchesAnOffset) && (
        <div className={styles['exactTime']}>
          <label className={styles['exactLabel']} htmlFor="log-flow-exact-time">
            Exact date and time
          </label>
          <input
            id="log-flow-exact-time"
            type="datetime-local"
            className={styles['exactInput']}
            value={toLocalInput(at)}
            onChange={(event) => {
              const parsed = Date.parse(event.target.value)
              if (Number.isFinite(parsed)) {
                /*
                 * `datetime-local` carries no zone, so the browser parses it in
                 * the *device's* zone. That is right for the normal case — the
                 * user is typing a wall-clock time where they are — and the
                 * captured `timeZone` is preserved either way, which is what
                 * keeps the day boundary correct (T-101).
                 */
                onChange({ epochMs: parsed, timeZone: at.timeZone })
              }
            }}
          />
        </div>
      )}
    </Sheet>
  )
}

/** `<input type="datetime-local">` wants wall-clock text with no zone. */
function toLocalInput(timestamp: Timestamp): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timestamp.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(timestamp.epochMs)
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '00'
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}

/**
 * The chip's label: "Now · 07:42 · change", or the full date once backdated.
 *
 * The date appears only when it is not today's, because a date on every entry
 * is noise and a date on a backdated one is the whole point.
 */
export function timeChipLabel(at: Timestamp, now: Timestamp): string {
  const backdated = Math.abs(now.epochMs - at.epochMs) > BACKDATED_MS
  const when = backdated
    ? `${formatDate(at)} · ${formatTime(at)}`
    : `Now · ${formatTime(at)}`
  return `${when} · change`
}
